import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { LoadingService } from './loading.service';
import { AppComponentName, Message, MessageActionType, MessageInfo, MessageSearchCriteria, MessageType, standardHttpOptions, MessageNotesSearchCriteria as MessageNoteSearchCriteria } from '@models';
import { throwError } from 'rxjs';
import { Observable } from 'rxjs';
import { KigSettings, KIG_SETTINGS } from '../../../settings/kig-settings';
import moment from 'moment';
import { UserService } from '@services/user.service';
import { of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';


@Injectable()

export class AppMessageService {
  public standardHttpOptionsWithUser: any;

  private apiUrl: string;

  constructor(private http: HttpClient,
              private loadingService: LoadingService,
              private userService: UserService,
              @Inject(KIG_SETTINGS) private settings: KigSettings) {
    this.apiUrl = this.settings.apiUrl;
  }

  buildStandardHttpOptionsWithUser(username: string) {
    this.standardHttpOptionsWithUser = {
      withCredentials: true,
      headers: new HttpHeaders({
        ['Content-Type']: 'application/json',
        username
      })
    };
  }

  public getAppMessages(searchCriteria: MessageSearchCriteria): Observable<any> {
    this.loadingService.setMessage('Retrieving logs ...');
    return this.http.post(`${ this.apiUrl }/message/betweendates`, JSON.stringify(searchCriteria), standardHttpOptions).pipe(
      map((response: Response) => response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public getMessageNotes(searchCriteria: MessageNoteSearchCriteria): Observable<any> {
    return this.http.post(`${ this.apiUrl }/message/notes`, JSON.stringify(searchCriteria), standardHttpOptions).pipe(
      map((response: Response) => response),
      catchError(this.handleError)
    );
  }


  public createAppMessage(message: Message) {
    return this.http.post(`${ this.apiUrl }/message`, JSON.stringify(this.mapMessage2MessageInfo(message)), standardHttpOptions).pipe(
      map((response: Response) => response == null ? 'success' : response),
      catchError(this.handleError)
    );
  }


  public updateMessage(messageInfo: MessageInfo) {
    return this.http.put(`${this.apiUrl}/message/`, JSON.stringify(messageInfo), standardHttpOptions).pipe(
      map((response: Response) => response == null ? 'success' : response),
      catchError(this.handleError)
    );
  }


  public deleteAppMessage(message: Message, username: string) {
    this.loadingService.setMessage('Deleting message ...');
    this.buildStandardHttpOptionsWithUser(username);
    return this.http.delete(`${ this.apiUrl }/message/${ message.id }`, this.standardHttpOptionsWithUser).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()),
    );
  }


  public mapMessage2MessageInfo(message: Message): MessageInfo {
    const messageInfo: MessageInfo = {
      id: message.id,
      messageType: Object.values(MessageType).indexOf(message.messageType),
      createdDate: message.createdDate,
      appComponentName: Object.values(AppComponentName).indexOf(message.appComponentName),
      actionType: Object.values(MessageActionType).indexOf(message.actionType),
      appComponentItem: message.appComponentItem,
      details: message.details,
      userNotes: message.userNotes,
      count: message.count,
      acknowledged: message.acknowledged,
      userName: message.userName,
      userGroupId: message.userGroupId
    };
    return messageInfo;
  }


  public mapMessageInfo2Message(messageInfo: MessageInfo): Message {
    const message: Message = {
      id: messageInfo.id,
      messageType: Object.values(MessageType)[messageInfo.messageType],
      createdDate: moment.utc(messageInfo.createdDate).local().toDate(),
      appComponentName: Object.values(AppComponentName)[messageInfo.appComponentName],
      actionType: Object.values(MessageActionType)[messageInfo.actionType],
      appComponentItem: messageInfo.appComponentItem,
      details: messageInfo.details,
      userNotes: messageInfo.userNotes,
      count: messageInfo.count,
      acknowledged: messageInfo.acknowledged,
      userName: messageInfo.userName.split('@')[0],
      userGroupId: messageInfo.userGroupId
    };
    return message;
  }


  public mapMessageInfos2Messages(messageInfos: MessageInfo[]): Message[] {
    const messages: Message[] = [];
    messageInfos.forEach(m => messages.push(this.mapMessageInfo2Message(m)) );
    return messages;
  }


  private handleError(error: any) {
    if (error.status === 404) {
      return of(null);
    }
    return throwError(() => error);
  }


}
